import { useCallback, useEffect, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import Loading from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { getGridcoDailyPeak, getGridcoMonthlyPeak, getGridcoScheduleDailyPeak, getGridcoScheduleMonthlyPeak } from "../../../Rest_api/RestApi";
import { R_CHART_TYPES } from "../const";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useSelector } from "react-redux";

let X_AXIS = [
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" }
];

const UNITS = ["MW", "MWh"];

const TYPE = ["Drawal", "Schedule"];

function getMonthWithYear(month: number, year: number): string {
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const yearShort = year.toString().slice(-2);
  return `${monthNames[month - 1]}-${yearShort}`;
}

function parseMonthYear(monthYearStr: string): { month: number; year: number } {
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const [monthStr, yearStr] = monthYearStr.split('-');
  const month = monthNames.indexOf(monthStr) + 1;
  const year = 2000 + parseInt(yearStr);
  return { month, year };
}

interface GridcoPeakAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  selectedUnit: string;
  onUnitChange: (unit: string) => void;
}

export function GridcoPeakAnalysis({ 
  onXAxisModeChange,
  selectedUnit,
  onUnitChange 
}: GridcoPeakAnalysisProps) {
  const [TableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const [drawalData, setDrawalData] = useState<any[]>([]);
  const [scheduleData, setScheduleData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[2]);
  const [selectedXaxis, setSelectedXaxis] = useState(X_AXIS[0]);
  const [selectedType, setSelectedType] = useState<string[]>([TYPE[0]]);
  const [loading, setLoading] = useState(true);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );

  const createChartObject = (
    key: string,
    suffix: string,
    displayName: string
  ) => {
    return {
      dataKey: `${key}_${suffix}`,
      name: `${key.replace("_", " ").replace("undefined", "")} ${displayName}`,
      type: selectedChartType.value,
      stackId: selectedChartType.stackId !== undefined ? "peak" : undefined,
    };
  };

  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab]?.defaultRangeDate === undefined) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const dates = DefaultDates[activeTab].defaultRangeDate;
      const startDate = dates.startDate.toISOString().split("T")[0];
      const endDate = dates.endDate.toISOString().split("T")[0];

      // Always fetch both drawal and schedule data
      if (selectedXaxis.key === "month") {
        const [drawalMonthData, scheduleMonthData] = await Promise.all([
          getGridcoMonthlyPeak(startDate, endDate),
          getGridcoScheduleMonthlyPeak(startDate, endDate)
        ]);

        const formattedDrawalData = drawalMonthData.map((item) => ({
          ...item,
          month: getMonthWithYear(item.month, item.year),
        }));

        const formattedScheduleData = scheduleMonthData.map((item) => ({
          ...item,
          month: getMonthWithYear(item.month, item.year),
        }));

        setDrawalData(formattedDrawalData);
        setScheduleData(formattedScheduleData);
      } else {
        const [drawalDailyData, scheduleDailyData] = await Promise.all([
          getGridcoDailyPeak(startDate, endDate),
          getGridcoScheduleDailyPeak(startDate, endDate)
        ]);

        setDrawalData(drawalDailyData);
        setScheduleData(scheduleDailyData);
      }
    } catch (error) {
      console.error("Error in data fetching:", error);
      setDrawalData([]);
      setScheduleData([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab, selectedXaxis.key]);

  useEffect(() => {
    // Determine which datasets to use based on selected type
    const isDrawalSelected = selectedType.includes("Drawal");
    const isScheduleSelected = selectedType.includes("Schedule");

    let combinedData: any[] = [];

    if (isDrawalSelected && isScheduleSelected) {
      // Merge both datasets
      combinedData = mergeDrawalAndScheduleData(drawalData, scheduleData);
    } else if (isDrawalSelected) {
      combinedData = drawalData;
    } else if (isScheduleSelected) {
      // Rename schedule fields to have schedule_ prefix for consistency
      combinedData = scheduleData.map(item => ({
        ...item,
        schedule_peak_mw: item.peak_mw,
        schedule_morning_peak_mw: item.morning_peak_mw,
        schedule_evening_peak_mw: item.evening_peak_mw,
        schedule_average_mw: item.average_mw,
      }));
    }

    if (combinedData.length === 0) {
      setChartData([]);
      setTableData([]);
      setChartKeys([]);
      return;
    }

    // Sort the data
    let filteredData = [...combinedData];
    filteredData.sort((a, b) => {
      if (selectedXaxis.key === "month") {
        const aMonthYear = parseMonthYear(a.month);
        const bMonthYear = parseMonthYear(b.month);
        
        if (aMonthYear.year !== bMonthYear.year) {
          return aMonthYear.year - bMonthYear.year;
        }
        return aMonthYear.month - bMonthYear.month;
      } else {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      }
    });

    // Apply unit conversion
    const convertedData = filteredData.map((item) => {
      const conversionFactor = selectedUnit === "MWh" ? 0.25 : 1;
      const convertedItem: any = {
        ...item,
      };

      // Convert drawal fields if present
      if (item.peak_mw !== undefined) {
        convertedItem.peak_mw = item.peak_mw * conversionFactor;
      }
      if (item.morning_peak_mw !== undefined) {
        convertedItem.morning_peak_mw = item.morning_peak_mw * conversionFactor;
      }
      if (item.evening_peak_mw !== undefined) {
        convertedItem.evening_peak_mw = item.evening_peak_mw * conversionFactor;
      }
      if (item.average_mw !== undefined) {
        convertedItem.average_mw = item.average_mw * conversionFactor;
      }

      // Convert schedule fields if present
      if (item.schedule_peak_mw !== undefined) {
        convertedItem.schedule_peak_mw = item.schedule_peak_mw * conversionFactor;
      }
      if (item.schedule_morning_peak_mw !== undefined) {
        convertedItem.schedule_morning_peak_mw = item.schedule_morning_peak_mw * conversionFactor;
      }
      if (item.schedule_evening_peak_mw !== undefined) {
        convertedItem.schedule_evening_peak_mw = item.schedule_evening_peak_mw * conversionFactor;
      }
      if (item.schedule_average_mw !== undefined) {
        convertedItem.schedule_average_mw = item.schedule_average_mw * conversionFactor;
      }

      return convertedItem;
    });

    // Determine aggregation keys based on selected type
    const aggregationKeys: string[] = [];
    if (isDrawalSelected) {
      aggregationKeys.push("peak_mw", "morning_peak_mw", "evening_peak_mw");
    }
    if (isScheduleSelected) {
      aggregationKeys.push("schedule_peak_mw", "schedule_morning_peak_mw", "schedule_evening_peak_mw");
    }

    const par: ChartDataParams = {
      keys: selectedXaxis.values,
      tableKeys: selectedXaxis.key === "date" ? ["date"] : ["month", "year"],
      groupBy: [], 
      volumeKey: ``,
      aggregationKeys: aggregationKeys,
      averageKeys: [],
      averageDuplicationKeys: [],
      priceKey: "",
      data: convertedData,
    };

    const cData = FormatChartData(par);
    setTableData(cData.tableData || []);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [drawalData, scheduleData, selectedXaxis, selectedUnit, selectedType]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  function mergeDrawalAndScheduleData(drawal: any[], schedule: any[]) {
    const mergedMap = new Map();

    // Add all drawal data
    drawal.forEach(item => {
      const key = selectedXaxis.key === "date" ? item.date : item.month;
      mergedMap.set(key, { ...item });
    });

    // Merge schedule data
    schedule.forEach(item => {
      const key = selectedXaxis.key === "date" ? item.date : item.month;
      const existing = mergedMap.get(key);
      
      if (existing) {
        mergedMap.set(key, {
          ...existing,
          schedule_peak_mw: item.peak_mw,
          schedule_morning_peak_mw: item.morning_peak_mw,
          schedule_evening_peak_mw: item.evening_peak_mw,
          schedule_average_mw: item.average_mw,
        });
      } else {
        mergedMap.set(key, {
          ...item,
          schedule_peak_mw: item.peak_mw,
          schedule_morning_peak_mw: item.morning_peak_mw,
          schedule_evening_peak_mw: item.evening_peak_mw,
          schedule_average_mw: item.average_mw,
        });
      }
    });

    return Array.from(mergedMap.values());
  }

  function getLegends() {
    const legends: any[] = [];
    const isDrawalSelected = selectedType.includes("Drawal");
    const isScheduleSelected = selectedType.includes("Schedule");

    if (isDrawalSelected) {
      const drawalSuffixes = [
        { suffix: "peak_mw", displayName: "Day Peak (Drawal)" },
        { suffix: "morning_peak_mw", displayName: "Morning Peak (Drawal)" },
        { suffix: "evening_peak_mw", displayName: "Evening Peak (Drawal)" },
      ];

      drawalSuffixes.forEach(({ suffix, displayName }) => {
        chartKeys.forEach((key) => {
          legends.push(createChartObject(key, suffix, displayName));
        });
      });
    }

    if (isScheduleSelected) {
      const scheduleSuffixes = [
        { suffix: "schedule_peak_mw", displayName: "Day Peak (Schedule)" },
        { suffix: "schedule_morning_peak_mw", displayName: "Morning Peak (Schedule)" },
        { suffix: "schedule_evening_peak_mw", displayName: "Evening Peak (Schedule)" },
      ];

      scheduleSuffixes.forEach(({ suffix, displayName }) => {
        chartKeys.forEach((key) => {
          legends.push(createChartObject(key, suffix, displayName));
        });
      });
    }

    return legends;
  }

  function handleTypeChange(item: string, index: number) {
    const selectedItem = TYPE[index];
    
    if (selectedType.includes(selectedItem)) {
      // Don't allow deselecting if it's the only selected item
      if (selectedType.length === 1) {
        return;
      }
      setSelectedType(selectedType.filter(t => t !== selectedItem));
    } else {
      setSelectedType([...selectedType, selectedItem]);
    }
  }

  function summaryExtraKeys() {
    if (selectedXaxis.key === "date") {
      return ["date"];
    } else {
      return ["month", "year"];
    }
  }

  const summaryExtraLabels = selectedXaxis.key === "date" 
    ? ["Date"]
    : ["Month", "Year"];

  const xDataKey = selectedXaxis.values[0];
  
  const commonSummaryProps = {
    legends: getLegends() as any,
    data: chartData,
    tableData: TableData,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys(),
    extraLabels: summaryExtraLabels,
    filename: "Gridco Load Peak Analysis",
  };

  return (
    <div className="gas-page-container">
      {loading && <Loading />}
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={["Peak Analysis"]} 
            onSelect={onXAxisModeChange}
          />

          <FilterMenu
            title="Intervals"
            items={X_AXIS.map((item) => item.label)}
            selectedItems={[selectedXaxis.label]}
            onSelect={(item, index) => {
              setSelectedXaxis(X_AXIS[index]);
            }}
          />
          <FilterMenu
            title="Unit"
            items={UNITS.map((item) => item)}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => onUnitChange(item)}
          />
          <FilterMenu
            title="Type"
            items={TYPE}
            selectedItems={selectedType}
            onSelect={handleTypeChange}
          />
          <FilterMenu
            title="Chart Type"
            items={R_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) =>
              setSelectedChartType(R_CHART_TYPES[index])
            }
          />
        </div>
      </NewFilterContainer>
      <MainPageContainer>
        <SecondaryHeader />
        <div className="gas-chart-container">
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading w-100">Discom Load Peak Analysis ({selectedUnit})</h2>
            <div className="flex gap-x-6 justify-end">
              <button
                onClick={() => {
                  dataTab.label === "Chart" &&
                    downloadChartTableCSV(commonSummaryProps);

                  dataTab.label === "Summary" &&
                    downloadSummaryCSV(commonSummaryProps);

                  dataTab.label === "Table" &&
                    downloadChartTableCSV(commonSummaryProps);
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>
          
          {dataTab.label === "Chart" && (
            <ReMixChart
              data={chartData}
              showBrush={undefined}
              isTimeSlot={false}
              onlyTitle={true}
              barCategoryGap={10}
              unit={selectedUnit}
              yAxisLabel={selectedUnit}
              legends={getLegends() as any}
              xDataKey={xDataKey}
              secondXDataKey={undefined}
            />
          )}
          {dataTab.label === "Summary" && <LegendSummaryTable {...commonSummaryProps} />}
          {dataTab.label === "Table" && <ChartTable {...commonSummaryProps} />}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={<p>Gridco : BSP bills & ABT Meters</p>}
              text="1. BSP Bills : Annexure 1 is used for cumulation.
              2. Some meter numbers in Annexure 1 were missing in provided ABT Meter files."
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
